import { Empty, Typography } from "antd";
import { SettingsIcon } from "components/Icons";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import AnyaAgentsState from "views/Automations/AnyaAgents/components/AnyaAgentsState";
import "views/Dashboards/AnyaProse.less";
import "views/Dashboards/AnyaStatusText.less";
import StreamingText from "views/Dashboards/StreamingText";

import ACDropdown, { ACDropdownOptionProps } from "ac-components/ACDropdown";
import ACButton from "ac-components/ac-button/ACButton";
import AnyaChatLoadingIcon from "ac-components/ac-icons/AnyaChatLoadingIcon";
import AnyaIcon from "ac-components/ac-icons/AnyaIcon";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import TickIcon from "ac-components/ac-icons/TickIcon";
import ACMiddleEllipsis from "ac-components/ac-middle-ellipsis/ACMiddleEllipsis";

import { useFindingDetails } from "../FindingDetailsProvider";
import useFindingAnyaAgentsTab from "./useFindingAnyaAgentsTab";

const alwaysIncludeOption = () => true;
const AGENTS_BUTTON_WIDTH = 150;

const AnyaAgentsTab = memo(function AnyaAgentsTab() {
  const { id } = useFindingDetails();
  const history = useHistory();
  const {
    agentsLoading,
    agentsError,
    agentOptions,
    updateSearch,
    selectedAgent,
    selectAgent,
    refreshAgents,
    agentResponse,
    agentResponseLoading,
    agentResponseError,
    agentResponseExists,
    retryAgentResponse,
  } = useFindingAnyaAgentsTab(id);
  const [searchText, setSearchText] = useState("");

  const openManageAgents = useCallback(() => {
    history.push("/automations/anya-agents");
  }, [history]);

  const agentMenuItems = useMemo<ACDropdownOptionProps[]>(() => {
    const footerItems: ACDropdownOptionProps[] = [
      {
        type: "divider",
        filterOption: alwaysIncludeOption,
      },
      {
        label: "Manage Agents",
        value: "manage-agents",
        filterOption: alwaysIncludeOption,
        icon: <SettingsIcon className="color-supporting" />,
      },
    ];

    if (agentsLoading) {
      return [
        {
          label: "Loading agents",
          value: "loading",
          disabled: true,
          filterOption: alwaysIncludeOption,
        },
        ...footerItems,
      ];
    }

    if (agentsError) {
      return [
        {
          label: agentsError,
          value: "error",
          disabled: true,
          filterOption: alwaysIncludeOption,
        },
        {
          label: "Retry",
          value: "retry",
          filterOption: alwaysIncludeOption,
        },
        ...footerItems,
      ];
    }

    const hasSearchText = Boolean(searchText.trim());
    const noMatchItems: ACDropdownOptionProps[] =
      hasSearchText && !agentOptions.length
        ? [
            {
              type: "label",
              label: "No match found",
              filterOption: alwaysIncludeOption,
            },
          ]
        : [];

    const items: ACDropdownOptionProps[] = agentOptions.length
      ? agentOptions.map((agent) => {
          const isSelected = String(selectedAgent?.id) === String(agent.id);

          return {
            label: (
              <span style={{ maxWidth: AGENTS_BUTTON_WIDTH + 50 }}>
                <ACMiddleEllipsis text={agent.name} applyWidth />
              </span>
            ),
            labelString: agent.name,
            value: String(agent.id),
            secondaryIcon: isSelected ? <TickIcon /> : undefined,
          };
        })
      : [
          {
            label: "No agents found",
            value: "empty",
            disabled: true,
          },
        ];

    return hasSearchText && !agentOptions.length
      ? [...noMatchItems, ...footerItems]
      : [...items, ...footerItems];
  }, [agentOptions, agentsError, agentsLoading, searchText, selectedAgent]);

  const onSearchTextChange = useCallback(
    (value: string) => {
      setSearchText(value);
      updateSearch(value);
    },
    [updateSearch],
  );

  const onAgentMenuOptionClick = useCallback(
    (value: string | number) => {
      if (value === "manage-agents") {
        openManageAgents();
        return;
      }

      if (value === "retry") {
        refreshAgents();
        return;
      }

      selectAgent(value);
    },
    [openManageAgents, refreshAgents, selectAgent],
  );

  const selector = (
    <div>
      <div className="d-flex-inline w-100">
        <ACDropdown
          items={agentMenuItems}
          placement="bottomLeft"
          onOptionClick={onAgentMenuOptionClick}
          allowSearch
          onSearchTextChange={onSearchTextChange}
          offsetY={8}
          menuWidth={AGENTS_BUTTON_WIDTH + 100}
        >
          <ACButton
            type="outline"
            rightIcon={<ChevronDownIcon className="color-supporting" />}
            className="text-left"
          >
            <div className="d-flex">
              <span className="text-bold">Agent:</span>
              <span className="m-l-xs" style={{ width: AGENTS_BUTTON_WIDTH }}>
                <ACMiddleEllipsis
                  text={selectedAgent?.name || "Select agent"}
                  applyWidth
                />
              </span>
            </div>
          </ACButton>
        </ACDropdown>
      </div>
    </div>
  );

  let content = null;

  if (agentsLoading && !selectedAgent) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Typography.Text className="system-process">
          Loading Anya agents <AnyaChatLoadingIcon />
        </Typography.Text>
      </div>
    );
  } else if (agentsError && !agentOptions.length) {
    content = (
      <AnyaAgentsState
        title="Unable to load agents"
        description={agentsError}
        actions={
          <ACButton type="ghost" size="sm" onClick={refreshAgents}>
            Retry
          </ACButton>
        }
        variant="error"
      />
    );
  } else if (!selectedAgent) {
    content = (
      <AnyaAgentsState
        title="No default agent available"
        description="Search and select an agent to load finding-specific output."
        icon={<AnyaIcon className="font-size-32 color-light" />}
        variant="muted"
      />
    );
  } else if (agentResponseLoading) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Typography.Text className="system-process">
          Loading agent output <AnyaChatLoadingIcon />
        </Typography.Text>
      </div>
    );
  } else if (agentResponseError) {
    content = (
      <AnyaAgentsState
        title="Unable to load agent output"
        description={agentResponseError}
        actions={
          <ACButton type="ghost" size="sm" onClick={retryAgentResponse}>
            Retry
          </ACButton>
        }
        variant="error"
      />
    );
  } else if (!agentResponseExists) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Empty description="No output available for this agent" />
      </div>
    );
  } else if (!agentResponse?.reasoningSummary && !agentResponse?.details) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Empty description="Select an agent to view finding insights" />
      </div>
    );
  } else {
    content = (
      <div className="anya-prose">
        <div className="w-100">
          {agentResponse?.details ? (
            <StreamingText
              text={agentResponse.details}
              stream={false}
              onComplete={() => {}}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="p-xs">
      {selector}
      {content}
    </div>
  );
});

export default AnyaAgentsTab;
